import React, { useState, useEffect, useRef } from 'react';
import { reservationApi } from '../api/reservationApi';
import { loadPaymentWidget, PaymentWidgetInstance } from '@tosspayments/payment-widget-sdk';

interface ExtendReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservation: {
    id: number;
    reservation_number: string;
    vehicle_name: string;
    end_date: string;
    return_location?: string;
  };
  onSuccess: () => void;
}

const ExtendReservationModal: React.FC<ExtendReservationModalProps> = ({
  isOpen,
  onClose,
  reservation,
  onSuccess,
}) => {
  const [newEndDate, setNewEndDate] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [availabilityResult, setAvailabilityResult] = useState<{
    available: boolean;
    message: string;
    additional_amount?: number;
    rental_fee?: number;
    insurance_fee?: number;
    extension_hours?: number;
    extension_days?: number;
    weekday_rate?: number;
    weekend_rate?: number;
  } | null>(null);
  const [error, setError] = useState('');

  // Payment Widget 관련 상태
  const paymentWidgetRef = useRef<PaymentWidgetInstance | null>(null);
  const paymentMethodsWidgetRef = useRef<any>(null);
  const [isLoadingWidget, setIsLoadingWidget] = useState(false);
  const [isPaymentReady, setIsPaymentReady] = useState(false);

  // 모달이 열릴 때 기본 연장 시간 설정 (현재 반납시간 +1시간)
  useEffect(() => {
    if (isOpen && reservation) {
      const currentEndDate = new Date(reservation.end_date);
      const defaultNewEndDate = new Date(currentEndDate.getTime() + 60 * 60 * 1000); // +1시간

      // 날짜 설정 (YYYY-MM-DD 형식)
      const dateString = defaultNewEndDate.toISOString().split('T')[0];
      setNewEndDate(dateString);

      // 시간 설정 (HH:MM 형식)
      const hours = defaultNewEndDate.getHours().toString().padStart(2, '0');
      const minutes = defaultNewEndDate.getMinutes().toString().padStart(2, '0');
      setNewEndTime(`${hours}:${minutes}`);
    }
  }, [isOpen, reservation]);

  // Payment Widget 초기화
  useEffect(() => {
    if (!isOpen || !showPayment || !availabilityResult?.additional_amount) return;

    const initializePaymentWidget = async () => {
      setIsLoadingWidget(true);
      try {
        const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY;
        if (!clientKey) {
          setError('결제 설정이 올바르지 않습니다.');
          return;
        }

        const paymentWidget = await loadPaymentWidget(clientKey, 'ANONYMOUS');
        paymentWidgetRef.current = paymentWidget;

        // additional_amount는 useEffect 조건에서 이미 체크되었으므로 확실히 존재함
        const amount = availabilityResult.additional_amount!;

        const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
          '#payment-widget',
          { value: amount },
          { variantKey: 'DEFAULT' }
        );
        paymentMethodsWidgetRef.current = paymentMethodsWidget;

        paymentWidget.renderAgreement('#agreement', { variantKey: 'DEFAULT' });

        paymentMethodsWidget.on('ready', () => {
          console.log('Payment widget is ready');
          setIsPaymentReady(true);
          setIsLoadingWidget(false);
        });
      } catch (error) {
        console.error('Payment widget initialization error:', error);
        setError('결제 위젯 로드 중 오류가 발생했습니다.');
        setIsLoadingWidget(false);
      }
    };

    initializePaymentWidget();

    return () => {
      paymentWidgetRef.current = null;
      paymentMethodsWidgetRef.current = null;
      setIsPaymentReady(false);
    };
  }, [isOpen, showPayment, availabilityResult]);

  if (!isOpen) return null;

  const handleCheckAvailability = async () => {
    if (!newEndDate || !newEndTime) {
      setError('새로운 반납일시를 선택해주세요.');
      return;
    }

    setError('');
    setIsChecking(true);
    setAvailabilityResult(null);

    try {
      // KST를 UTC로 변환
      const kstDateTime = new Date(`${newEndDate}T${newEndTime}:00+09:00`);
      const newEndDateTime = kstDateTime.toISOString();

      const result = await reservationApi.checkExtensionAvailability(
        reservation.id,
        newEndDateTime
      );

      console.log('API Response:', result);
      setAvailabilityResult({
        available: result.available,
        message: result.message,
        additional_amount: result.additional_amount,
        rental_fee: result.rental_fee,
        insurance_fee: result.insurance_fee,
        extension_hours: result.extension_hours,
        extension_days: result.extension_days,
        weekday_rate: result.weekday_rate,
        weekend_rate: result.weekend_rate,
      });
    } catch (err: any) {
      console.error('가용성 확인 실패:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        setError(errorDetail.map((e: any) => e.msg).join(', '));
      } else if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError('가용성 확인 중 오류가 발생했습니다.');
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handleProceedToPayment = () => {
    setShowPayment(true);
  };

  const handleBackToCheck = () => {
    setShowPayment(false);
    setIsPaymentReady(false);
    paymentWidgetRef.current = null;
    paymentMethodsWidgetRef.current = null;
  };

  const handlePayment = async () => {
    const paymentWidget = paymentWidgetRef.current;

    if (!paymentWidget) {
      alert('결제 위젯을 불러오는 중입니다. 잠시만 기다려주세요.');
      return;
    }

    if (!isPaymentReady) {
      alert('결제 UI를 준비하고 있습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (!availabilityResult?.additional_amount) {
      setError('결제 금액을 확인할 수 없습니다.');
      return;
    }

    try {
      const orderId = `EXT_${reservation.reservation_number}_${Date.now()}`;

      // 연장 기간을 "1일 17시간" 또는 "1.5시간" 형식으로 표시
      const totalHours = availabilityResult.extension_hours || 0;
      const days = Math.floor(totalHours / 24);
      const remainingHours = totalHours % 24;
      let periodText = '';
      if (days > 0 && remainingHours > 0) {
        periodText = `${days}일 ${remainingHours}시간`;
      } else if (days > 0) {
        periodText = `${days}일`;
      } else {
        periodText = `${remainingHours}시간`;
      }

      const orderName = `${reservation.vehicle_name} 예약 연장 (${periodText})`;

      // KST를 UTC로 변환
      const kstDateTime = new Date(`${newEndDate}T${newEndTime}:00+09:00`);
      const newEndDateTimeUTC = kstDateTime.toISOString();

      await paymentWidget.requestPayment({
        orderId: orderId,
        orderName: orderName,
        successUrl: `${window.location.origin}/extension-payment-success?reservationId=${reservation.id}&newEndDate=${encodeURIComponent(newEndDateTimeUTC)}`,
        failUrl: `${window.location.origin}/extension-payment-fail`,
      });
    } catch (error) {
      console.error('Payment request error:', error);
      setError('결제 요청 중 오류가 발생했습니다.');
    }
  };

  const currentEndDate = new Date(reservation.end_date);
  const minDate = new Date(currentEndDate.getTime() + 60 * 60 * 1000); // 1시간 후

  // 시간 옵션 생성 (09:00 ~ 21:00, 30분 단위)
  const timeOptions = [];
  for (let hour = 9; hour <= 21; hour++) {
    timeOptions.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 21) {
      timeOptions.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }

  // 선택된 날짜에서 사용 가능한 시간인지 체크
  const isTimeDisabled = (time: string) => {
    if (!newEndDate) return false;

    const [hours, minutes] = time.split(':').map(Number);
    const selectedDateTime = new Date(newEndDate);
    selectedDateTime.setHours(hours, minutes, 0, 0);

    // 현재 반납시간 +1시간 이전이면 disabled
    return selectedDateTime < minDate;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">
            {showPayment ? '결제하기' : '예약 연장'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!showPayment ? (
          /* 1단계: 연장 가능 여부 확인 */
          <div className="space-y-4">
            {/* 예약 정보 */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">예약번호</p>
              <p className="font-semibold text-gray-900">{reservation.reservation_number}</p>
              <p className="text-sm text-gray-600 mt-2 mb-1">차량</p>
              <p className="font-semibold text-gray-900">{reservation.vehicle_name}</p>
              {reservation.return_location && (
                <>
                  <p className="text-sm text-gray-600 mt-2 mb-1">반납위치</p>
                  <p className="font-semibold text-gray-900">{reservation.return_location}</p>
                </>
              )}
              <p className="text-sm text-gray-600 mt-2 mb-1">현재 반납시간</p>
              <p className="font-semibold text-blue-600">
                {new Date(reservation.end_date).toLocaleString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'Asia/Seoul',
                })}
              </p>
            </div>

            {/* 새로운 반납일시 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                새로운 반납일시
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={newEndDate}
                  onChange={(e) => {
                    setNewEndDate(e.target.value);
                    setAvailabilityResult(null);
                  }}
                  min={minDate.toISOString().split('T')[0]}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={newEndTime}
                  onChange={(e) => {
                    setNewEndTime(e.target.value);
                    setAvailabilityResult(null);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">시간 선택</option>
                  {timeOptions.map((time) => (
                    <option key={time} value={time} disabled={isTimeDisabled(time)}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 가용성 확인 버튼 */}
            <button
              onClick={handleCheckAvailability}
              disabled={isChecking || !newEndDate || !newEndTime}
              className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isChecking ? '확인 중...' : '연장 가능 여부 확인'}
            </button>

            {/* 가용성 결과 */}
            {availabilityResult && (
              <div
                className={`p-4 rounded-lg ${
                  availabilityResult.available
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                <p
                  className={`text-sm font-medium mb-2 ${
                    availabilityResult.available ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {availabilityResult.message}
                </p>
                {availabilityResult.available && availabilityResult.additional_amount !== undefined && (
                  <div className="mt-3 pt-3 border-t border-green-300">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">연장 기간:</span>
                      <span className="font-medium text-gray-900">
                        {(() => {
                          const totalHours = availabilityResult.extension_hours || 0;
                          const days = Math.floor(totalHours / 24);
                          const remainingHours = totalHours % 24;
                          if (days > 0 && remainingHours > 0) {
                            return `${days}일 ${remainingHours}시간`;
                          } else if (days > 0) {
                            return `${days}일`;
                          } else {
                            return `${remainingHours}시간`;
                          }
                        })()}
                      </span>
                    </div>
                    {availabilityResult.rental_fee !== undefined && (
                      <div className="flex justify-between text-sm mb-1 mt-2">
                        <span className="text-gray-700">렌트비:</span>
                        <span className="font-medium text-gray-900">{availabilityResult.rental_fee.toLocaleString()}원</span>
                      </div>
                    )}
                    {availabilityResult.insurance_fee !== undefined && availabilityResult.insurance_fee > 0 && (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-700">보험료:</span>
                        <span className="font-medium text-gray-900">{availabilityResult.insurance_fee.toLocaleString()}원</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base mt-2 pt-2 border-t border-green-300">
                      <span className="font-semibold text-gray-900">추가 결제금액:</span>
                      <span className="font-bold text-green-700 text-lg">{availabilityResult.additional_amount.toLocaleString()}원</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 에러 메시지 */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* 버튼 */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleProceedToPayment}
                disabled={
                  !availabilityResult?.available ||
                  !availabilityResult?.additional_amount ||
                  !newEndDate ||
                  !newEndTime
                }
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                다음: 결제하기
              </button>
            </div>
          </div>
        ) : (
          /* 2단계: 결제 위젯 */
          <div className="space-y-4">
            {/* 예약 정보 요약 */}
            <div className="bg-gray-50 p-4 rounded-lg text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">차량:</span>
                <span className="font-medium">{reservation.vehicle_name}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">연장 기간:</span>
                <span className="font-medium">
                  {(() => {
                    const totalHours = availabilityResult?.extension_hours || 0;
                    const days = Math.floor(totalHours / 24);
                    const hours = totalHours % 24;
                    if (days > 0 && hours > 0) {
                      return `${days}일 ${hours}시간`;
                    } else if (days > 0) {
                      return `${days}일`;
                    } else {
                      return `${hours}시간`;
                    }
                  })()}
                </span>
              </div>
              {availabilityResult?.rental_fee !== undefined && (
                <div className="flex justify-between mb-1 mt-2">
                  <span className="text-gray-600">렌트비:</span>
                  <span className="font-medium">{availabilityResult.rental_fee.toLocaleString()}원</span>
                </div>
              )}
              {availabilityResult?.insurance_fee !== undefined && availabilityResult.insurance_fee > 0 && (
                <div className="flex justify-between mb-1">
                  <span className="text-gray-600">보험료:</span>
                  <span className="font-medium">{availabilityResult.insurance_fee.toLocaleString()}원</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-300 mt-2 font-bold text-base">
                <span>결제 금액:</span>
                <span className="text-blue-600">{availabilityResult?.additional_amount?.toLocaleString()}원</span>
              </div>
            </div>

            {/* 로딩 상태 */}
            {isLoadingWidget && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Payment Widget 영역 */}
            <div className={isLoadingWidget ? 'hidden' : ''}>
              <div id="payment-widget" className="mb-4"></div>
              <div id="agreement" className="mb-4"></div>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* 버튼 */}
            <div className="flex gap-2">
              <button
                onClick={handleBackToCheck}
                className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-300 transition-colors"
              >
                이전
              </button>
              <button
                onClick={handlePayment}
                disabled={isLoadingWidget || !isPaymentReady}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold text-lg"
              >
                {isLoadingWidget || !isPaymentReady
                  ? '결제 준비 중...'
                  : `${availabilityResult?.additional_amount?.toLocaleString()}원 결제하기`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtendReservationModal;
